import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { trackFeedbackSubmit } from '../ga';
import './FeedbackModal.css';

/**
 * @param {{ onClose: () => void }} props
 */
export function FeedbackModal({ onClose }) {
  const titleId = useId();
  const messageId = useId();
  const messageRef = useRef(null);
  const { pathname, search } = useLocation();

  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    setMessage('');
    setError(null);
    setNotice(null);
    setSubmitting(false);
    const t = window.setTimeout(() => {
      messageRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = message.trim();
    if (trimmed.length < 3) {
      setError('Please enter at least 3 characters.');
      return;
    }
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const pagePath = `${pathname}${search}`;
      const res = await fetch('/api/feedback.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed, pagePath }),
      });
      const body = await jsonOrThrow(res);
      const n =
        body &&
        typeof body === 'object' &&
        typeof body.notice === 'string' &&
        body.notice.trim() !== ''
          ? body.notice.trim()
          : 'Thank you — your feedback has been received.';
      setNotice(n);
      trackFeedbackSubmit();
    } catch (err) {
      setError(describeLoadFailure(err));
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = message.trim().length >= 3;

  return createPortal(
    <div
      className="feedback-modal__backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="feedback-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="feedback-modal__header">
          <h2 id={titleId} className="feedback-modal__title">
            Send feedback
          </h2>
          <button
            type="button"
            className="feedback-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="feedback-modal__body">
          {notice == null ? (
            <>
              <p className="feedback-modal__lede">
                Share your thoughts, ideas, or issues. Your feedback is
                anonymous — no account or email is required.
              </p>
              {error !== null && (
                <p
                  className="feedback-modal__banner feedback-modal__banner--error"
                  role="alert"
                >
                  {error}
                </p>
              )}
              <form onSubmit={handleSubmit} noValidate>
                <div className="feedback-modal__field">
                  <label htmlFor={messageId}>Your feedback</label>
                  <textarea
                    ref={messageRef}
                    id={messageId}
                    value={message}
                    onChange={(ev) => setMessage(ev.target.value)}
                    placeholder="What can we improve?"
                    maxLength={4000}
                    disabled={submitting}
                    rows={5}
                    required
                  />
                </div>
                <div className="feedback-modal__actions">
                  <button
                    type="submit"
                    className="feedback-modal__submit"
                    disabled={submitting || !canSubmit}
                  >
                    {submitting ? 'Sending…' : 'Send feedback'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <p
                className="feedback-modal__banner feedback-modal__banner--success"
                role="status"
              >
                {notice}
              </p>
              <div className="feedback-modal__actions">
                <button
                  type="button"
                  className="feedback-modal__submit"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
